/**
 * Landing v3 — the dot world map under the social-proof headline, shared by
 * the desktop scene and the phone layout.
 */
import { motion } from 'framer-motion';

import { cn } from '@/lib/utils';

import { WORLD_DOT_COLS, WORLD_DOT_GRID, WORLD_DOT_ROWS } from './landing-world-dots';

/**
 * The map is a Mercator dot raster (the projection the Figma map is drawn in)
 * with its own pitch, at the design's ~790px width — nothing about it derives
 * from the page background. Dots at ~0.36 of the pitch, as in the design.
 */
const MAP_PITCH = 10.4;
const MAP_DOT_R = 3.7;
const MAP_W = WORLD_DOT_COLS * MAP_PITCH;
const MAP_H = WORLD_DOT_ROWS * MAP_PITCH;
const MAP_WAVES = 14;

/**
 * The lit dots, decoded once from the packed grid: cell (x, y) → its centre
 * on the map's own pitch. `wave` sequences the illumination west→east with a
 * little jitter.
 */
const WORLD_DOTS = (() => {
  const dots: { cx: number; cy: number; hl: boolean; wave: number }[] = [];
  WORLD_DOT_GRID.forEach((row, y) => {
    for (let x = 0; x < row.length; x += 1) {
      const cell = row[x];
      if (cell === '.') continue;
      const jitter = ((x * 7 + y * 13) % 10) / 10;
      const wave = Math.min(
        MAP_WAVES - 1,
        Math.floor(((x / WORLD_DOT_COLS) * 0.78 + jitter * 0.22) * MAP_WAVES),
      );
      dots.push({
        cx: (x + 0.5) * MAP_PITCH,
        cy: (y + 0.5) * MAP_PITCH,
        hl: cell === '2',
        wave,
      });
    }
  });
  return dots;
})();

/** Dots grouped per wave, so the reveal costs 14 animated groups rather than
 *  ~1.4k animated circles. */
const WORLD_WAVES = Array.from({ length: MAP_WAVES }, (_, w) =>
  WORLD_DOTS.filter((d) => d.wave === w),
);

/**
 * The world map, lit up. Highlighted regions (Europe, North & South America,
 * Australia) use `primary-600` at a slightly larger radius; the rest of the
 * land uses `primary-300` — both DS ramps, so they invert for dark mode.
 * Illumination is TRIGGERED: once the section arrives, the waves light
 * west→east on their own timeline.
 */
export function WorldDotMap({
  arrived,
  reduceMotion,
  className,
}: {
  arrived: boolean;
  reduceMotion: boolean;
  /** `h-auto w-full` scales the map to its box (the phone layout). */
  className?: string;
}) {
  return (
    <svg
      width={MAP_W}
      height={MAP_H}
      viewBox={`0 0 ${MAP_W} ${MAP_H}`}
      aria-hidden
      className={cn('block max-w-full', className)}>
      {WORLD_WAVES.map((dots, w) => (
        <motion.g
          key={w}
          initial={{ opacity: 0 }}
          animate={{ opacity: arrived ? 1 : 0 }}
          transition={
            reduceMotion
              ? { duration: 0 }
              : { duration: 0.5, delay: arrived ? 0.15 + w * 0.055 : 0, ease: 'easeOut' }
          }>
          {dots.map((d) => (
            <circle
              key={`${d.cx}-${d.cy}`}
              cx={d.cx}
              cy={d.cy}
              r={d.hl ? MAP_DOT_R * 1.15 : MAP_DOT_R}
              className={d.hl ? 'fill-primary-600' : 'fill-primary-300'}
            />
          ))}
        </motion.g>
      ))}
    </svg>
  );
}
